import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getAllUsers } from "../../../../services/CommonService";
import moment from 'moment';
import { exportToExcel } from "../../../../Utils/common-Excel";

const Users = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10; // show more users per page

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await getAllUsers();

            if (!response || response.status !== 200) {
                throw new Error('Failed to fetch users');
            }

            setContacts(response.data || []);
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err?.response?.data?.message || err?.message,
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    // Search filter ONLY (no date restriction)
    const filteredContacts = contacts.filter(contact =>
        contact?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact?.phone?.includes(searchTerm)
    );

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentContacts = filteredContacts?.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredContacts?.length / itemsPerPage);

    // Excel download
    const handleDownloadExcel = () => {
        if (!filteredContacts?.length) {
            Swal.fire("No Data", "No users to export", "info");
            return;
        }

        const exportData = filteredContacts?.map((item, index) => ({
            "S.No": index + 1,
            Name: item.name,
            Email: item.email,
            Mobile: item.phone,
            "Created On": moment(item.created_at).format('DD-MM-YYYY'),
            "Updated On": moment(item.updated_at).format('DD-MM-YYYY')
        }));

        exportToExcel(exportData, 'UserList', 'User', 'Users Report');
    };

    if (loading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <div className="container-fluid mt-4">
            <div className="card shadow">
                <div className="card-header adminCardHeader text-white">
                    <h4 className="mb-0">Users</h4>
                </div>

                <div className="card-body">
                    {/* Search & Export */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <label className="me-2">Search:</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ width: '200px' }}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="col-md-6 text-end">
                            <button
                                className="btn btn-success"
                                onClick={handleDownloadExcel}
                            >
                                <i className="bi bi-file-earmark-excel-fill me-2"></i>
                                Excel
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>S.No</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Created On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentContacts.length ? (
                                    currentContacts.map((contact, i) => (
                                        <tr key={contact.id}>
                                            <td>{indexOfFirstItem + i + 1}</td>
                                            <td>{contact.name}</td>
                                            <td>{contact.email}</td>
                                            <td>{contact.phone}</td>
                                            <td>{moment(contact.created_at).format('DD-MM-YYYY')}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center">
                                            No users found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <nav>
                            <ul className="pagination pagination-sm justify-content-end">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {[...Array(totalPages)].map((_, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}

                    {/* Footer info */}
                    <div className="text-center text-muted mt-2">
                        Showing {currentContacts.length} of {filteredContacts.length} users
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Users;
